import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useSignalR } from '../../hooks/useSignalR';
import { issueReportsAPI, userManagementAPI } from '../../services/api';
import { useTranslation } from 'react-i18next';
import './IssueReports.css';

const IssueReports = () => {
    const { t } = useTranslation(['issueReports', 'common']);
    const { user } = useAuth();
    const { subscribe, unsubscribe, joinIssueRoom, leaveIssueRoom } = useSignalR();

    // State management
    const [issues, setIssues] = useState([]);
    const [selectedIssue, setSelectedIssue] = useState(null);
    const [messages, setMessages] = useState([]);
    const [statistics, setStatistics] = useState(null);
    const [loading, setLoading] = useState(false);
    const [messagesLoading, setMessagesLoading] = useState(false);
    const [error, setError] = useState(null);

    // Filter states
    const [statusFilter, setStatusFilter] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('');
    const [screenFilter, setScreenFilter] = useState('');
    const [readStatusFilter, setReadStatusFilter] = useState('all'); // 'all', 'read', 'unread'
    const [assigneeFilter, setAssigneeFilter] = useState('all'); // 'all', 'myself', 'ppd', 'school', 'admin'
    const [ppdFilter, setPpdFilter] = useState('');
    const [schoolFilter, setSchoolFilter] = useState('');
    const [assignedToFilter, setAssignedToFilter] = useState('');
    const [searchKeyword, setSearchKeyword] = useState('');
    const [reportIdSearch, setReportIdSearch] = useState('');

    // Sort states
    const [sortField, setSortField] = useState('date'); // 'date', 'reportId'
    const [sortDirection, setSortDirection] = useState('desc'); // 'asc', 'desc'

    // Dropdown options for filters (Admin/SuperAdmin only)
    const [districts, setDistricts] = useState([]);
    const [schools, setSchools] = useState([]);

    // Pagination states
    const [page, setPage] = useState(1);
    const [pageSize] = useState(20);
    const [totalCount, setTotalCount] = useState(0);
    const [totalPages, setTotalPages] = useState(0);

    // Message form state
    const [messageText, setMessageText] = useState('');
    const [isInternalNote, setIsInternalNote] = useState(false);
    const [sendingMessage, setSendingMessage] = useState(false);

    // Status management state
    const [showStatusModal, setShowStatusModal] = useState(false);
    const [newStatus, setNewStatus] = useState('');
    const [resolutionNotes, setResolutionNotes] = useState('');
    const [updatingStatus, setUpdatingStatus] = useState(false);

    // Assignment modal state
    const [showAssignModal, setShowAssignModal] = useState(false);
    const [assignToRole, setAssignToRole] = useState('');
    const [assigning, setAssigning] = useState(false);

    // Load issues on mount and when filters change
    useEffect(() => {
        loadIssues();
        loadStatistics();
    }, [page, pageSize, statusFilter, categoryFilter, screenFilter, readStatusFilter, assigneeFilter, ppdFilter, schoolFilter, assignedToFilter, sortField, sortDirection]);

    // Load districts and schools for Admin/SuperAdmin filters
    useEffect(() => {
        if (user?.role === 'ADMIN' || user?.role === 'SUPERADMIN') {
            loadDistricts();
            loadSchools(); // Load all schools initially
        }
    }, [user]);

    // Load schools when PPD filter changes
    useEffect(() => {
        if ((user?.role === 'ADMIN' || user?.role === 'SUPERADMIN') && ppdFilter) {
            loadSchools(ppdFilter);
            setSchoolFilter(''); // Reset school filter when PPD changes
        }
    }, [ppdFilter]);

    // SignalR real-time event listeners
    useEffect(() => {
        // Handler for new issues - refresh issues list
        const handleNewIssue = (notification) => {
            console.log('[IssueReports] Received new issue notification:', notification);
            loadIssues();
            loadStatistics();
        };

        // Handler for new messages - reload messages if viewing that issue
        const handleNewMessage = (notification) => {
            console.log('[IssueReports] Received new message notification:', notification);

            // Refresh issues list to update message count
            loadIssues();

            // If the message is for the currently selected issue, reload messages
            if (selectedIssue && notification.reportId === selectedIssue.reportId) {
                loadMessages(selectedIssue.reportId);
            }
        };

        // Handler for issue-specific messages (when in issue room)
        const handleIssueMessage = (data) => {
            console.log('[IssueReports] Received issue-specific message:', data);

            // If the message is for the currently selected issue, reload messages
            if (selectedIssue && data.reportId === selectedIssue.reportId) {
                loadMessages(selectedIssue.reportId);
            }
        };

        // Handler for status changes - refresh issues list
        const handleStatusChange = (notification) => {
            console.log('[IssueReports] Received status change notification:', notification);
            loadIssues();
            loadStatistics();

            // Update selected issue status if it matches
            if (selectedIssue && notification.reportId === selectedIssue.reportId) {
                setSelectedIssue(prev => ({
                    ...prev,
                    status: notification.newStatus
                }));
            }
        };

        // Subscribe to SignalR events
        subscribe('newIssue', handleNewIssue);
        subscribe('newMessage', handleNewMessage);
        subscribe('newIssueMessage', handleIssueMessage);
        subscribe('statusChange', handleStatusChange);

        // Cleanup subscriptions on unmount
        return () => {
            unsubscribe('newIssue', handleNewIssue);
            unsubscribe('newMessage', handleNewMessage);
            unsubscribe('newIssueMessage', handleIssueMessage);
            unsubscribe('statusChange', handleStatusChange);
        };
    }, [selectedIssue, subscribe, unsubscribe]);

    // Join/leave issue room when selectedIssue changes
    useEffect(() => {
        if (selectedIssue) {
            // Join the issue room for real-time updates
            joinIssueRoom(selectedIssue.reportId);

            // Leave the room when component unmounts or issue changes
            return () => {
                leaveIssueRoom(selectedIssue.reportId);
            };
        }
    }, [selectedIssue, joinIssueRoom, leaveIssueRoom]);

    const loadDistricts = async () => {
        try {
            const response = await userManagementAPI.getDistricts();
            if (response.success && response.data) {
                setDistricts(response.data);
            }
        } catch (err) {
            console.error('Error loading districts:', err);
            setDistricts([]);
        }
    };

    const loadSchools = async (district = null) => {
        try {
            const response = await userManagementAPI.getSchools(district);
            if (response.success && response.data) {
                setSchools(response.data);
            }
        } catch (err) {
            console.error('Error loading schools:', err);
            setSchools([]);
        }
    };

    const loadIssues = async () => {
        setLoading(true);
        setError(null);

        try {
            const filter = {
                status: statusFilter || null,
                category: categoryFilter || null,
                screen: screenFilter || null,
                readStatus: readStatusFilter || 'all',
                assigneeFilter: assigneeFilter || 'all',
                ppdDistrict: ppdFilter || null,
                schoolCode: schoolFilter || null,
                assignedToRole: assignedToFilter === 'UNASSIGNED' ? null : assignedToFilter || null,
                searchKeyword: searchKeyword || null,
                reportId: reportIdSearch || null,
                page,
                pageSize
            };

            const response = await issueReportsAPI.getIssueReports(filter);

            if (response.data) {
                // Sort issues based on sortField and sortDirection
                const sortedIssues = (response.data || []).sort((a, b) => {
                    let comparison = 0;

                    if (sortField === 'reportId') {
                        comparison = a.reportId - b.reportId;
                    } else {
                        // Default sort by date
                        const dateA = new Date(a.createdDate);
                        const dateB = new Date(b.createdDate);
                        comparison = dateA - dateB;
                        // If dates are equal, sort by ID
                        if (comparison === 0) {
                            comparison = a.reportId - b.reportId;
                        }
                    }

                    return sortDirection === 'desc' ? -comparison : comparison;
                });

                setIssues(sortedIssues);
                setTotalCount(response.totalCount || 0);
                setTotalPages(response.totalPages || 0);
            }
        } catch (err) {
            console.error('Error loading issues:', err);
            setError(err.message || t('issueReports:error.loadIssues'));
            setIssues([]);
        } finally {
            setLoading(false);
        }
    };

    const loadStatistics = async () => {
        try {
            const response = await issueReportsAPI.getStatistics();
            if (response.success && response.data) {
                setStatistics(response.data);
            }
        } catch (err) {
            console.error('Error loading statistics:', err);
        }
    };

    const selectIssue = async (issue) => {
        try {
            // Load full issue details including screenshot
            const detailsResponse = await issueReportsAPI.getIssueReport(issue.reportId);
            if (detailsResponse.success && detailsResponse.data) {
                setSelectedIssue(detailsResponse.data);
            } else {
                // Fallback to list data if details fail to load
                setSelectedIssue(issue);
            }

            await loadMessages(issue.reportId);

            // Mark as read
            await issueReportsAPI.markAsRead(issue.reportId);

            // Update the issue in the list to reflect read status immediately
            setIssues(prevIssues =>
                prevIssues.map(i =>
                    i.reportId === issue.reportId
                        ? { ...i, isReadByCurrentUser: true }
                        : i
                )
            );

            loadStatistics(); // Refresh unread count
        } catch (err) {
            console.error('Error loading issue details:', err);
            // Fallback to list data
            setSelectedIssue(issue);
            await loadMessages(issue.reportId);
        }
    };

    const loadMessages = async (reportId) => {
        setMessagesLoading(true);
        try {
            const response = await issueReportsAPI.getMessages(reportId);
            if (response.success && response.data) {
                setMessages(response.data);
            }
        } catch (err) {
            console.error('Error loading messages:', err);
            setMessages([]);
        } finally {
            setMessagesLoading(false);
        }
    };

    const handleSendMessage = async (e) => {
        e.preventDefault();

        if (!messageText.trim() || !selectedIssue) {
            return;
        }

        setSendingMessage(true);
        try {
            const messageData = {
                messageText: messageText.trim(),
                isInternalNote: user?.role === 'ADMIN' || user?.role === 'SUPERADMIN' ? isInternalNote : false
            };

            const response = await issueReportsAPI.sendMessage(selectedIssue.reportId, messageData);

            if (response.success) {
                setMessageText('');
                setIsInternalNote(false);
                await loadMessages(selectedIssue.reportId);
                await loadIssues(); // Refresh list to update last message info
            }
        } catch (err) {
            console.error('Error sending message:', err);
            alert(t('issueReports:error.sendMessage') + ' ' + err.message);
        } finally {
            setSendingMessage(false);
        }
    };

    const handleUpdateStatus = async () => {
        if (!newStatus || !selectedIssue) {
            return;
        }

        setUpdatingStatus(true);
        try {
            const statusData = {
                status: newStatus,
                resolutionNotes: newStatus === 'RESOLVED' || newStatus === 'CLOSED' ? resolutionNotes : null
            };

            const response = await issueReportsAPI.updateStatus(selectedIssue.reportId, statusData);

            if (response.success) {
                setShowStatusModal(false);
                setNewStatus('');
                setResolutionNotes('');
                await loadIssues();

                // Refresh selected issue
                const updatedIssue = issues.find(i => i.reportId === selectedIssue.reportId);
                if (updatedIssue) {
                    setSelectedIssue({ ...selectedIssue, status: newStatus });
                }
            }
        } catch (err) {
            console.error('Error updating status:', err);
            alert(t('issueReports:error.updateStatus') + ' ' + err.message);
        } finally {
            setUpdatingStatus(false);
        }
    };

    const handleAssignIssue = async () => {
        if (!assignToRole || !selectedIssue) {
            return;
        }

        setAssigning(true);
        try {
            const response = await issueReportsAPI.assignIssue(selectedIssue.reportId, assignToRole);

            if (response.success) {
                setShowAssignModal(false);
                setAssignToRole('');
                await loadIssues();

                // Refresh selected issue
                const updatedIssue = issues.find(i => i.reportId === selectedIssue.reportId);
                if (updatedIssue) {
                    setSelectedIssue({ ...selectedIssue, assignedToRole: assignToRole });
                }
            }
        } catch (err) {
            console.error('Error assigning issue:', err);
            alert(t('issueReports:error.assignIssue') + ' ' + err.message);
        } finally {
            setAssigning(false);
        }
    };

    const handleSearch = () => {
        setPage(1);
        loadIssues();
    };

    const handleReset = () => {
        setStatusFilter('');
        setCategoryFilter('');
        setScreenFilter('');
        setReadStatusFilter('all');
        setAssigneeFilter('all');
        setPpdFilter('');
        setSchoolFilter('');
        setAssignedToFilter('');
        setSearchKeyword('');
        setReportIdSearch('');
        setSortField('date');
        setSortDirection('desc');
        setPage(1);
        // Reload all schools when PPD filter is cleared
        if (user?.role === 'ADMIN' || user?.role === 'SUPERADMIN') {
            loadSchools();
        }
    };

    // Toggle sort direction or change sort field
    const handleSort = (field) => {
        if (sortField === field) {
            // Toggle direction if same field
            setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            // Change field and set to descending
            setSortField(field);
            setSortDirection('desc');
        }
    };

    const canManageIssue = () => {
        return user?.role === 'HEADMASTER' || user?.role === 'PPD' ||
               user?.role === 'ADMIN' || user?.role === 'SUPERADMIN';
    };

    const getStatusBadgeClass = (status) => {
        const statusMap = {
            'NEW': 'status-new',
            'IN_PROGRESS': 'status-in-progress',
            'PENDING': 'status-pending',
            'RESOLVED': 'status-resolved',
            'CLOSED': 'status-closed'
        };
        return statusMap[status] || 'status-default';
    };

    const getCategoryIcon = (category) => {
        const categoryMap = {
            'BUG': '🐛',
            'FEATURE_REQUEST': '✨',
            'DATA_ISSUE': '📊',
            'UI_ISSUE': '🎨',
            'PERFORMANCE': '⚡',
            'OTHER': '❓'
        };
        return categoryMap[category] || '📝';
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleString('en-MY', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <div className="issue-reports-container">
            {/* Header with Logo and Title */}
            <div className="header-container">
                <img className="logo" src="/KTC.png" alt="KTC" />
                <h2 title={t('issueReports:title', { lng: 'en' })}>{t('issueReports:title')} ({t('issueReports:titleMs')})</h2>
                <div className="header-buttons">
                    {/* Placeholder for future buttons */}
                </div>
            </div>

            {/* Statistics Cards */}
            <div className="issue-reports-content-wrapper">
                {statistics && (
                    <div className="statistics-cards">
                        <div className="stat-card" title={t('issueReports:statistics.totalIssues', { lng: 'en' })}>
                            <div className="stat-value">{statistics.totalIssues}</div>
                            <div className="stat-label">{t('issueReports:statistics.totalIssues')}</div>
                        </div>
                        <div className="stat-card stat-new" title={t('issueReports:statistics.new', { lng: 'en' })}>
                            <div className="stat-value">{statistics.newIssues}</div>
                            <div className="stat-label">{t('issueReports:statistics.new')}</div>
                        </div>
                        <div className="stat-card stat-progress" title={t('issueReports:statistics.inProgress', { lng: 'en' })}>
                            <div className="stat-value">{statistics.inProgressIssues}</div>
                            <div className="stat-label">{t('issueReports:statistics.inProgress')}</div>
                        </div>
                        <div className="stat-card stat-unread" title={t('issueReports:statistics.unread', { lng: 'en' })}>
                            <div className="stat-value">{statistics.unreadIssues}</div>
                            <div className="stat-label">{t('issueReports:statistics.unread')}</div>
                        </div>
                    </div>
                )}

                {/* Filters Section */}
                <div className="filters-section">
                <div className="filters-row">
                    <select
                        className="filter-select"
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        title={t('issueReports:filters.allStatus', { lng: 'en' })}
                    >
                        <option value="">{t('issueReports:filters.allStatus')}</option>
                        <option value="NEW">{t('issueReports:filters.statuses.NEW')}</option>
                        <option value="IN_PROGRESS">{t('issueReports:filters.statuses.IN_PROGRESS')}</option>
                        <option value="PENDING">{t('issueReports:filters.statuses.PENDING')}</option>
                        <option value="RESOLVED">{t('issueReports:filters.statuses.RESOLVED')}</option>
                        <option value="CLOSED">{t('issueReports:filters.statuses.CLOSED')}</option>
                    </select>

                    <select
                        className="filter-select"
                        value={categoryFilter}
                        onChange={(e) => setCategoryFilter(e.target.value)}
                        title={t('issueReports:filters.allCategories', { lng: 'en' })}
                    >
                        <option value="">{t('issueReports:filters.allCategories')}</option>
                        <option value="BUG">{t('issueReports:filters.categories.BUG')}</option>
                        <option value="FEATURE_REQUEST">{t('issueReports:filters.categories.FEATURE_REQUEST')}</option>
                        <option value="DATA_ISSUE">{t('issueReports:filters.categories.DATA_ISSUE')}</option>
                        <option value="UI_ISSUE">{t('issueReports:filters.categories.UI_ISSUE')}</option>
                        <option value="PERFORMANCE">{t('issueReports:filters.categories.PERFORMANCE')}</option>
                        <option value="OTHER">{t('issueReports:filters.categories.OTHER')}</option>
                    </select>

                    <select
                        className="filter-select"
                        value={readStatusFilter}
                        onChange={(e) => setReadStatusFilter(e.target.value)}
                        title={t('issueReports:filters.readStatus.all', { lng: 'en' })}
                    >
                        <option value="all">{t('issueReports:filters.readStatus.all', 'All')}</option>
                        <option value="unread">{t('issueReports:filters.readStatus.unread', 'Unread')}</option>
                        <option value="read">{t('issueReports:filters.readStatus.read', 'Read')}</option>
                    </select>

                    <select
                        className="filter-select"
                        value={assigneeFilter}
                        onChange={(e) => setAssigneeFilter(e.target.value)}
                        title={t('issueReports:filters.assignee.all', { lng: 'en' })}
                    >
                        <option value="all">{t('issueReports:filters.assignee.all', 'All Issues')}</option>
                        <option value="myself">{t('issueReports:filters.assignee.myself', 'My Issues')}</option>
                        <option value="ppd">{t('issueReports:filters.assignee.ppd', 'Assigned to PPD')}</option>
                        <option value="school">{t('issueReports:filters.assignee.school', 'Assigned to School')}</option>
                        <option value="admin">{t('issueReports:filters.assignee.admin', 'Assigned to Admin')}</option>
                    </select>

                    <select
                        className="filter-select"
                        value={assignedToFilter}
                        onChange={(e) => setAssignedToFilter(e.target.value)}
                        title={t('issueReports:filters.allAssignments', { lng: 'en' })}
                    >
                        <option value="">{t('issueReports:filters.allAssignments')}</option>
                        <option value="UNASSIGNED">{t('issueReports:filters.unassigned')}</option>
                        <option value="HEADMASTER">{t('issueReports:filters.roles.HEADMASTER')}</option>
                        <option value="PPD">{t('issueReports:filters.roles.PPD')}</option>
                        <option value="ADMIN">{t('issueReports:filters.roles.ADMIN')}</option>
                    </select>

                    {/* PPD/District Filter - Admin/SuperAdmin Only */}
                    {(user?.role === 'ADMIN' || user?.role === 'SUPERADMIN') && (
                        <select
                            className="filter-select"
                            value={ppdFilter}
                            onChange={(e) => setPpdFilter(e.target.value)}
                        >
                            <option value="">All Districts / Semua Daerah</option>
                            {districts.map((district) => (
                                <option key={district} value={district}>
                                    {district}
                                </option>
                            ))}
                        </select>
                    )}

                    {/* School Filter - Admin/SuperAdmin Only */}
                    {(user?.role === 'ADMIN' || user?.role === 'SUPERADMIN') && (
                        <select
                            className="filter-select"
                            value={schoolFilter}
                            onChange={(e) => setSchoolFilter(e.target.value)}
                        >
                            <option value="">All Schools / Semua Sekolah</option>
                            {schools.map((school) => (
                                <option key={school.schoolCode} value={school.schoolCode}>
                                    {school.schoolCode} - {school.schoolName}
                                </option>
                            ))}
                        </select>
                    )}

                    <input
                        type="text"
                        className="filter-search filter-search-id"
                        placeholder={t('issueReports:filters.reportIdPlaceholder', 'Report ID (e.g., 123)')}
                        value={reportIdSearch}
                        onChange={(e) => setReportIdSearch(e.target.value.replace(/\D/g, ''))}
                        onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                        style={{ maxWidth: '150px', minWidth: '120px' }}
                    />

                    <input
                        type="text"
                        className="filter-search"
                        placeholder={t('issueReports:filters.searchPlaceholder')}
                        value={searchKeyword}
                        onChange={(e) => setSearchKeyword(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    />

                    <button className="btn btn-primary" onClick={handleSearch} title={t('issueReports:buttons.search', { lng: 'en' })}>
                        {t('issueReports:buttons.search')}
                    </button>
                    <button className="btn btn-secondary" onClick={handleReset} title={t('issueReports:buttons.reset', { lng: 'en' })}>
                        {t('issueReports:buttons.reset')}
                    </button>
                </div>

                {/* Sort Options */}
                <div className="sort-options" style={{ marginTop: '12px', display: 'flex', gap: '12px', alignItems: 'center' }}>
                    <span style={{ fontSize: '14px', color: '#666' }} title={t('issueReports:sort.label', { lng: 'en' })}>{t('issueReports:sort.label', 'Sort by')}:</span>
                    <button
                        className={`btn btn-sm ${sortField === 'date' ? 'btn-primary' : 'btn-secondary'}`}
                        onClick={() => handleSort('date')}
                        title={t('issueReports:sort.date', { lng: 'en' })}
                    >
                        {t('issueReports:sort.date', 'Date')} {sortField === 'date' && (sortDirection === 'desc' ? '↓' : '↑')}
                    </button>
                    <button
                        className={`btn btn-sm ${sortField === 'reportId' ? 'btn-primary' : 'btn-secondary'}`}
                        onClick={() => handleSort('reportId')}
                        title={t('issueReports:sort.reportId', { lng: 'en' })}
                    >
                        {t('issueReports:sort.reportId', 'Report ID')} {sortField === 'reportId' && (sortDirection === 'desc' ? '↓' : '↑')}
                    </button>
                </div>
            </div>

            {/* Main Content - Master-Detail Layout */}
            <div className="issue-reports-content">
                {/* Issues List (Master) */}
                <div className="issues-list-panel">
                    <div className="panel-header">
                        <h3 title={t('issueReports:issuesList.title', { lng: 'en' })}>{t('issueReports:issuesList.title')} ({totalCount})</h3>
                    </div>

                    {loading ? (
                        <div className="loading-state">{t('issueReports:loading')}</div>
                    ) : error ? (
                        <div className="error-state">{error}</div>
                    ) : issues.length === 0 ? (
                        <div className="empty-state">
                            <p>{t('issueReports:issuesList.noIssues')}</p>
                        </div>
                    ) : (
                        <div className="issues-list">
                            {issues.map((issue) => (
                                <div
                                    key={issue.reportId}
                                    className={`issue-item ${selectedIssue?.reportId === issue.reportId ? 'selected' : ''} ${!issue.isReadByCurrentUser ? 'unread' : ''}`}
                                    onClick={() => selectIssue(issue)}
                                >
                                    <div className="issue-header">
                                        <span className="issue-id">
                                            #{issue.reportId}
                                            {!issue.isReadByCurrentUser && (
                                                <span className="unread-badge">unread</span>
                                            )}
                                        </span>
                                        <span className={`status-badge ${getStatusBadgeClass(issue.status)}`}>
                                            {t(`issueReports:statusBadges.${issue.status}`)}
                                        </span>
                                    </div>

                                    <div className="issue-title">
                                        <span className="category-icon">{getCategoryIcon(issue.category)}</span>
                                        {issue.description.substring(0, 80)}
                                        {issue.description.length > 80 && '...'}
                                    </div>

                                    <div className="issue-meta">
                                        <span className="meta-item">
                                            <i className="icon-screen"></i> {issue.screen}
                                        </span>
                                        {issue.schoolName && (
                                            <span className="meta-item">
                                                <i className="icon-school"></i> {issue.schoolName}
                                            </span>
                                        )}
                                        {issue.assignedToRole && (
                                            <span className="meta-item assignee">
                                                <i className="icon-user"></i> {t(`issueReports:filters.roles.${issue.assignedToRole}`, issue.assignedToRole)}
                                            </span>
                                        )}
                                    </div>

                                    <div className="issue-footer">
                                        <span className="timestamp">{formatDate(issue.createdDate)}</span>
                                        {issue.messageCount > 0 && (
                                            <span className="message-count">
                                                💬 {issue.messageCount}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className="pagination">
                            <button
                                className="btn btn-sm"
                                onClick={() => setPage(p => Math.max(1, p - 1))}
                                disabled={page === 1}
                                title={t('issueReports:buttons.previous', { lng: 'en' })}
                            >
                                {t('issueReports:buttons.previous')}
                            </button>
                            <span className="page-info" title={`${t('issueReports:pagination.page', { lng: 'en' })} ${page} ${t('issueReports:pagination.of', { lng: 'en' })} ${totalPages}`}>
                                {t('issueReports:pagination.page')} {page} {t('issueReports:pagination.of')} {totalPages}
                            </span>
                            <button
                                className="btn btn-sm"
                                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                disabled={page === totalPages}
                                title={t('issueReports:buttons.next', { lng: 'en' })}
                            >
                                {t('issueReports:buttons.next')}
                            </button>
                        </div>
                    )}
                </div>

                {/* Issue Detail Panel */}
                <div className="issue-detail-panel">
                    {!selectedIssue ? (
                        <div className="empty-selection">
                            <p>{t('issueReports:issuesList.selectIssue')}</p>
                        </div>
                    ) : (
                        <div className="issue-detail">
                            {/* Detail Header */}
                            <div className="detail-header">
                                <div className="detail-title">
                                    <h2>
                                        {getCategoryIcon(selectedIssue.category)}
                                        {' '}{t('issueReports:issueDetail.issueNumber')}{selectedIssue.reportId}
                                    </h2>
                                    <span className={`status-badge ${getStatusBadgeClass(selectedIssue.status)}`}>
                                        {t(`issueReports:statusBadges.${selectedIssue.status}`)}
                                    </span>
                                </div>

                                {canManageIssue() && (
                                    <div className="detail-actions">
                                        <button
                                            className="btn btn-sm btn-secondary"
                                            onClick={() => setShowStatusModal(true)}
                                            title={t('issueReports:buttons.updateStatus', { lng: 'en' })}
                                        >
                                            {t('issueReports:buttons.updateStatus')}
                                        </button>
                                        <button
                                            className="btn btn-sm btn-secondary"
                                            onClick={() => setShowAssignModal(true)}
                                            title={t('issueReports:buttons.assign', { lng: 'en' })}
                                        >
                                            {t('issueReports:buttons.assign')}
                                        </button>
                                    </div>
                                )}
                            </div>

                            {/* Issue Information */}
                            <div className="issue-info">
                                <div className="info-section">
                                    <h3 title={t('issueReports:issueDetail.description', { lng: 'en' })}>{t('issueReports:issueDetail.description')}</h3>
                                    <p>{selectedIssue.description}</p>
                                </div>

                                <div className="info-grid">
                                    <div className="info-item">
                                        <label title={t('issueReports:issueDetail.category', { lng: 'en' })}>{t('issueReports:issueDetail.category')}</label>
                                        <span>{t(`issueReports:filters.categories.${selectedIssue.category}`)}</span>
                                    </div>
                                    <div className="info-item">
                                        <label title={t('issueReports:issueDetail.screen', { lng: 'en' })}>{t('issueReports:issueDetail.screen')}</label>
                                        <span>{selectedIssue.screen}</span>
                                    </div>
                                    <div className="info-item">
                                        <label title={t('issueReports:issueDetail.reporter', { lng: 'en' })}>{t('issueReports:issueDetail.reporter')}</label>
                                        <span>{selectedIssue.userEmail}</span>
                                    </div>
                                    <div className="info-item">
                                        <label title={t('issueReports:issueDetail.school', { lng: 'en' })}>{t('issueReports:issueDetail.school')}</label>
                                        <span>{selectedIssue.schoolName || t('issueReports:issueDetail.notApplicable')}</span>
                                    </div>
                                    <div className="info-item">
                                        <label title={t('issueReports:issueDetail.created', { lng: 'en' })}>{t('issueReports:issueDetail.created')}</label>
                                        <span>{formatDate(selectedIssue.createdDate)}</span>
                                    </div>
                                    <div className="info-item">
                                        <label title={t('issueReports:issueDetail.assignedTo', { lng: 'en' })}>{t('issueReports:issueDetail.assignedTo')}</label>
                                        <span>{selectedIssue.assignedToRole ? t(`issueReports:filters.roles.${selectedIssue.assignedToRole}`) : t('issueReports:issueDetail.unassigned')}</span>
                                    </div>
                                </div>

                                {selectedIssue.screenshotBase64 && (
                                    <div className="info-section">
                                        <h3>{t('issueReports:issueDetail.screenshot')}</h3>
                                        <img
                                            src={selectedIssue.screenshotBase64.startsWith('data:')
                                                ? selectedIssue.screenshotBase64
                                                : `data:image/png;base64,${selectedIssue.screenshotBase64}`
                                            }
                                            alt={t('issueReports:issueDetail.screenshot')}
                                            className="issue-screenshot"
                                            style={{ maxWidth: '100%', border: '1px solid #ddd', borderRadius: '4px' }}
                                        />
                                    </div>
                                )}

                                {/* Diagnostic Information - Only visible to SUPERADMIN */}
                                {user?.role === 'SUPERADMIN' && (
                                    <>
                                        {/* Console Logs */}
                                        {selectedIssue.consoleLogs && (
                                            <div className="info-section">
                                                <details className="diagnostic-details" open>
                                                    <summary><h3>{t('issueReports:issueDetail.consoleLogs')}</h3></summary>
                                                    <div className="diagnostic-data">
                                                        {(() => {
                                                            try {
                                                                const logs = JSON.parse(selectedIssue.consoleLogs);
                                                                return (
                                                                    <div className="console-logs">
                                                                        {logs.slice(-20).map((log, idx) => (
                                                                            <div key={idx} className={`log-entry log-${log.type}`}>
                                                                                <span className="log-time">{new Date(log.time).toLocaleTimeString()}</span>
                                                                                <span className="log-type">[{log.type.toUpperCase()}]</span>
                                                                                <span className="log-message">{log.message}</span>
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                );
                                                            } catch (e) {
                                                                return <p>{t('issueReports:issueDetail.unableToParse', { type: 'console logs' })}</p>;
                                                            }
                                                        })()}
                                                    </div>
                                                </details>
                                            </div>
                                        )}

                                        {/* JavaScript Errors */}
                                        {selectedIssue.jsErrors && (
                                            <div className="info-section">
                                                <details className="diagnostic-details">
                                                    <summary><h3>{t('issueReports:issueDetail.jsErrors')}</h3></summary>
                                                    <div className="diagnostic-data">
                                                        {(() => {
                                                            try {
                                                                const errors = JSON.parse(selectedIssue.jsErrors);
                                                                return errors.length > 0 ? (
                                                                    <div className="js-errors">
                                                                        {errors.map((error, idx) => (
                                                                            <div key={idx} className="error-entry">
                                                                                <div className="error-message">{error.message}</div>
                                                                                {error.stack && (
                                                                                    <pre className="error-stack">{error.stack}</pre>
                                                                                )}
                                                                                <div className="error-time">{new Date(error.time).toLocaleString()}</div>
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                ) : <p>{t('issueReports:issueDetail.noErrors')}</p>;
                                                            } catch (e) {
                                                                return <p>{t('issueReports:issueDetail.unableToParse', { type: 'JS errors' })}</p>;
                                                            }
                                                        })()}
                                                    </div>
                                                </details>
                                            </div>
                                        )}

                                        {/* Browser Info */}
                                        {selectedIssue.browserInfo && (
                                            <div className="info-section">
                                                <details className="diagnostic-details">
                                                    <summary><h3>{t('issueReports:issueDetail.browserInfo')}</h3></summary>
                                                    <div className="diagnostic-data">
                                                        {(() => {
                                                            try {
                                                                const browserInfo = JSON.parse(selectedIssue.browserInfo);
                                                                return (
                                                                    <div className="browser-info">
                                                                        <p><strong>{t('issueReports:issueDetail.browserDetails.browser')}</strong> {browserInfo.browser?.name} {browserInfo.browser?.version}</p>
                                                                        <p><strong>{t('issueReports:issueDetail.browserDetails.os')}</strong> {browserInfo.os?.name} {browserInfo.os?.version}</p>
                                                                        <p><strong>{t('issueReports:issueDetail.browserDetails.device')}</strong> {browserInfo.device?.type || t('issueReports:issueDetail.browserDetails.desktop')}</p>
                                                                    </div>
                                                                );
                                                            } catch (e) {
                                                                return <p>{t('issueReports:issueDetail.unableToParse', { type: 'browser info' })}</p>;
                                                            }
                                                        })()}
                                                    </div>
                                                </details>
                                            </div>
                                        )}

                                        {/* API Call History */}
                                        {selectedIssue.apiCallHistory && (
                                            <div className="info-section">
                                                <details className="diagnostic-details">
                                                    <summary><h3>{t('issueReports:issueDetail.apiCalls')}</h3></summary>
                                                    <div className="diagnostic-data">
                                                        {(() => {
                                                            try {
                                                                const apiCalls = JSON.parse(selectedIssue.apiCallHistory);
                                                                return apiCalls.length > 0 ? (
                                                                    <div className="api-calls">
                                                                        {apiCalls.slice(-10).map((call, idx) => (
                                                                            <div key={idx} className="api-call-entry">
                                                                                <span className="api-method">{call.method}</span>
                                                                                <span className="api-url">{call.url}</span>
                                                                                <span className={`api-status status-${call.status >= 200 && call.status < 300 ? 'success' : 'error'}`}>
                                                                                    {call.status}
                                                                                </span>
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                ) : <p>{t('issueReports:issueDetail.noApiCalls')}</p>;
                                                            } catch (e) {
                                                                return <p>{t('issueReports:issueDetail.unableToParse', { type: 'API call history' })}</p>;
                                                            }
                                                        })()}
                                                    </div>
                                                </details>
                                            </div>
                                        )}

                                        {/* Server Diagnostics */}
                                        {selectedIssue.serverDiagnostics && (
                                            <div className="info-section">
                                                <h3>{t('issueReports:issueDetail.serverDiagnostics')}</h3>
                                                <details className="diagnostic-details">
                                                    <summary>{t('issueReports:issueDetail.viewDiagnostics')}</summary>
                                                    <pre className="diagnostic-json">
                                                        {JSON.stringify(JSON.parse(selectedIssue.serverDiagnostics), null, 2)}
                                                    </pre>
                                                </details>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>

                            {/* Messages Section */}
                            <div className="messages-section">
                                <h3 title={t('issueReports:messages.title', { lng: 'en' })}>{t('issueReports:messages.title')}</h3>

                                {messagesLoading ? (
                                    <div className="loading-state">{t('issueReports:loadingMessages')}</div>
                                ) : (
                                    <div className="messages-list">
                                        {messages.length === 0 ? (
                                            <p className="no-messages">{t('issueReports:messages.noMessages')}</p>
                                        ) : (
                                            messages.map((msg) => (
                                                <div
                                                    key={msg.messageId}
                                                    className={`message-item ${msg.isInternalNote ? 'internal-note' : ''}`}
                                                >
                                                    <div className="message-header">
                                                        <span className="message-sender">
                                                            {msg.senderName || msg.senderEmail}
                                                            {' '}
                                                            <span className="sender-role">({msg.senderRole})</span>
                                                        </span>
                                                        <span className="message-time">
                                                            {formatDate(msg.createdDate)}
                                                        </span>
                                                    </div>
                                                    <div className="message-text">
                                                        {msg.isInternalNote && (
                                                            <span className="internal-badge">{t('issueReports:messages.internalNote')}</span>
                                                        )}
                                                        {msg.messageText}
                                                    </div>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                )}

                                {/* Message Form */}
                                <form className="message-form" onSubmit={handleSendMessage}>
                                    <textarea
                                        className="message-input"
                                        placeholder={t('issueReports:messages.messagePlaceholder')}
                                        value={messageText}
                                        onChange={(e) => setMessageText(e.target.value)}
                                        rows="3"
                                        required
                                    />

                                    <div className="message-form-footer">
                                        {(user?.role === 'ADMIN' || user?.role === 'SUPERADMIN') && (
                                            <label className="checkbox-label" title={t('issueReports:messages.internalNoteLabel', { lng: 'en' })}>
                                                <input
                                                    type="checkbox"
                                                    checked={isInternalNote}
                                                    onChange={(e) => setIsInternalNote(e.target.checked)}
                                                />
                                                {t('issueReports:messages.internalNoteLabel')}
                                            </label>
                                        )}

                                        <button
                                            type="submit"
                                            className="btn btn-primary"
                                            disabled={sendingMessage || !messageText.trim()}
                                            title={t('issueReports:buttons.sendMessage', { lng: 'en' })}
                                        >
                                            {sendingMessage ? t('issueReports:buttons.sending') : t('issueReports:buttons.sendMessage')}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Status Update Modal */}
            {showStatusModal && (
                <div className="modal-overlay" onClick={() => setShowStatusModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3 title={t('issueReports:modal.statusUpdate.title', { lng: 'en' })}>{t('issueReports:modal.statusUpdate.title')}</h3>
                            <button className="modal-close" onClick={() => setShowStatusModal(false)} title="Close">{t('issueReports:modal.close')}</button>
                        </div>

                        <div className="modal-body">
                            <div className="form-group">
                                <label title={t('issueReports:modal.statusUpdate.newStatus', { lng: 'en' })}>{t('issueReports:modal.statusUpdate.newStatus')}</label>
                                <select
                                    className="form-control"
                                    value={newStatus}
                                    onChange={(e) => setNewStatus(e.target.value)}
                                    title={t('issueReports:modal.statusUpdate.selectStatus', { lng: 'en' })}
                                >
                                    <option value="">{t('issueReports:modal.statusUpdate.selectStatus')}</option>
                                    <option value="NEW">{t('issueReports:filters.statuses.NEW')}</option>
                                    <option value="IN_PROGRESS">{t('issueReports:filters.statuses.IN_PROGRESS')}</option>
                                    <option value="PENDING">{t('issueReports:filters.statuses.PENDING')}</option>
                                    <option value="RESOLVED">{t('issueReports:filters.statuses.RESOLVED')}</option>
                                    <option value="CLOSED">{t('issueReports:filters.statuses.CLOSED')}</option>
                                </select>
                            </div>

                            {(newStatus === 'RESOLVED' || newStatus === 'CLOSED') && (
                                <div className="form-group">
                                    <label title={t('issueReports:modal.statusUpdate.resolutionNotes', { lng: 'en' })}>{t('issueReports:modal.statusUpdate.resolutionNotes')}</label>
                                    <textarea
                                        className="form-control"
                                        rows="4"
                                        value={resolutionNotes}
                                        onChange={(e) => setResolutionNotes(e.target.value)}
                                        placeholder={t('issueReports:modal.statusUpdate.resolutionPlaceholder')}
                                    />
                                </div>
                            )}
                        </div>

                        <div className="modal-footer">
                            <button
                                className="btn btn-secondary"
                                onClick={() => setShowStatusModal(false)}
                                title={t('issueReports:buttons.cancel', { lng: 'en' })}
                            >
                                {t('issueReports:buttons.cancel')}
                            </button>
                            <button
                                className="btn btn-primary"
                                onClick={handleUpdateStatus}
                                disabled={!newStatus || updatingStatus}
                                title={t('issueReports:buttons.update', { lng: 'en' })}
                            >
                                {updatingStatus ? t('issueReports:buttons.updating') : t('issueReports:buttons.update')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Assignment Modal */}
            {showAssignModal && (
                <div className="modal-overlay" onClick={() => setShowAssignModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3 title={t('issueReports:modal.assign.title', { lng: 'en' })}>{t('issueReports:modal.assign.title')}</h3>
                            <button className="modal-close" onClick={() => setShowAssignModal(false)} title="Close">{t('issueReports:modal.close')}</button>
                        </div>

                        <div className="modal-body">
                            <div className="form-group">
                                <label title={t('issueReports:modal.assign.assignToRole', { lng: 'en' })}>{t('issueReports:modal.assign.assignToRole')}</label>
                                <select
                                    className="form-control"
                                    value={assignToRole}
                                    onChange={(e) => setAssignToRole(e.target.value)}
                                    title={t('issueReports:modal.assign.selectRole', { lng: 'en' })}
                                >
                                    <option value="">{t('issueReports:modal.assign.selectRole')}</option>
                                    <option value="HEADMASTER">{t('issueReports:filters.roles.HEADMASTER')}</option>
                                    <option value="PPD">{t('issueReports:filters.roles.PPD')}</option>
                                    <option value="ADMIN">{t('issueReports:filters.roles.ADMIN')}</option>
                                </select>
                            </div>
                        </div>

                        <div className="modal-footer">
                            <button
                                className="btn btn-secondary"
                                onClick={() => setShowAssignModal(false)}
                                title={t('issueReports:buttons.cancel', { lng: 'en' })}
                            >
                                {t('issueReports:buttons.cancel')}
                            </button>
                            <button
                                className="btn btn-primary"
                                onClick={handleAssignIssue}
                                disabled={!assignToRole || assigning}
                                title={t('issueReports:buttons.assignIssue', { lng: 'en' })}
                            >
                                {assigning ? t('issueReports:buttons.assigning') : t('issueReports:buttons.assignIssue')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            </div>
        </div>
    );
};

export default IssueReports;
